import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link2, Calendar, Receipt, Loader2, X } from 'lucide-react';
import AttachLinkModal from './AttachLinkModal';
import { inputClass } from './caseConstants';

// What the case is about. A pre-sale enquiry usually has no booking or order
// yet, so the link can be attached (or swapped) from here.
export default function CaseLinkedEntityCard({ caseData, linkedBooking, linkedOrder, onDone }) {
  const [showAttach, setShowAttach] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const unlink = async (field) => {
    setBusy(true);
    setError('');
    try {
      const res = await base44.functions.invoke('caseLink', {
        caseId: caseData.id,
        ...(field === 'booking' ? { bookingId: '' } : { orderId: '' }),
      });
      if (res.data?.error) throw new Error(res.data.error);
      if (onDone) onDone();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not unlink.');
    }
    setBusy(false);
  };

  const nothingLinked = !linkedBooking && !linkedOrder;

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Link2 className="w-3.5 h-3.5 text-primary" />
          <p className="text-white text-[10px] font-bold tracking-widest">LINKED RECORD</p>
        </div>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
      </div>

      <div className="px-4 py-3 space-y-3">
        {nothingLinked && (
          <p className="text-white/30 text-xs py-2">
            Nothing linked yet — attach a booking or order so the resolution tools can act on it.
          </p>
        )}

        {linkedBooking && (
          <div className="border border-white/5 bg-[#0d0d0f] px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Calendar className="w-3.5 h-3.5 text-primary shrink-0" />
                <p className="text-white/70 text-[10px] font-bold tracking-widest">BOOKING</p>
              </div>
              <button
                onClick={() => unlink('booking')}
                className="text-white/25 hover:text-red-400 transition-colors shrink-0"
                aria-label="Unlink booking"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-white text-xs mt-2">
              {linkedBooking.date || 'No date'} · {linkedBooking.time_slot || '—'}
            </p>
            <p className="text-white/40 text-[11px] mt-1 truncate">
              {linkedBooking.experience_type || 'Session'} · {linkedBooking.rig_type || 'bay TBC'}
            </p>
            <p className="text-white/30 text-[10px] mt-1 uppercase tracking-wider">
              {linkedBooking.status || 'status unknown'}
            </p>
          </div>
        )}

        {linkedOrder && (
          <div className="border border-white/5 bg-[#0d0d0f] px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Receipt className="w-3.5 h-3.5 text-primary shrink-0" />
                <p className="text-white/70 text-[10px] font-bold tracking-widest">ORDER</p>
              </div>
              <button
                onClick={() => unlink('order')}
                className="text-white/25 hover:text-red-400 transition-colors shrink-0"
                aria-label="Unlink order"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-white text-xs mt-2">
              {linkedOrder.invoice_number || 'No invoice number'}
            </p>
            <p className="text-white/40 text-[11px] mt-1 truncate">
              {linkedOrder.product_name || 'Order'} · £{Number(linkedOrder.amount || 0).toFixed(2)}
            </p>
            <div className="flex items-center justify-between gap-2 mt-1">
              <p className="text-white/30 text-[10px] uppercase tracking-wider">
                {linkedOrder.status || 'status unknown'}
              </p>
              {linkedOrder.invoice_url && (
                <a
                  href={linkedOrder.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 text-[10px] font-bold tracking-wider transition-colors"
                >
                  VIEW INVOICE
                </a>
              )}
            </div>
          </div>
        )}

        {error && <p className="text-red-400 text-[11px]">{error}</p>}

        <button
          onClick={() => setShowAttach(true)}
          className="w-full border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors"
        >
          ATTACH / CHANGE LINK
        </button>
      </div>

      {showAttach && (
        <AttachLinkModal
          caseData={caseData}
          onClose={() => setShowAttach(false)}
          onDone={() => {
            setShowAttach(false);
            if (onDone) onDone();
          }}
        />
      )}
    </div>
  );
}